/* eslint-disable */
import * as React from "react";
import * as RechartsPrimitive from "recharts";

import { ITooltipOptions } from "./chart-options";

// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: "", dark: ".dark" } as const;

export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  );
};

type ChartContextProps = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextProps | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />");
  }

  return context;
}

function useUniqueId() {
  const idRef = React.useRef(0);

  React.useEffect(() => {
    idRef.current += 1;
  });

  return idRef.current;
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    config: ChartConfig;
    children: React.ComponentProps<
      typeof RechartsPrimitive.ResponsiveContainer
    >["children"];
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uniqueId = useUniqueId();
  const chartId = `chart-${id || uniqueId}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-chart={chartId}
        ref={ref}
        className={"chartContainer " + (className || "")}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = "Chart";

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([_, config]) => config.theme || config.color
  );

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
                .map(([key, itemConfig]) => {
                  const color =
                    itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
                    itemConfig.color;
                  return color ? `  --color-${key}: ${color};` : null;
                })
                .join("\n")}
}
`
          )
          .join("\n"),
      }}
    />
  );
};

const ChartTooltip = RechartsPrimitive.Tooltip;

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
  React.ComponentProps<"div"> & {
    hideLabel?: boolean;
    hideIndicator?: boolean;
    indicator?: "line" | "dot" | "dashed";
    nameKey?: string;
    labelKey?: string;
    heading?: ITooltipOptions["heading"];
  }
>(
  (
    {
      active,
      payload,
      className,
      indicator = "dot",
      hideIndicator = false,
      labelFormatter,
      color,
      nameKey,
      heading,
    },
    ref
  ) => {
    const { config } = useChart();

    if (!active || !payload?.length) {
      return null;
    }

    const headingText =
      heading?.key && payload[0]?.payload[heading.key] ||
      heading?.value ||
      undefined;

    return (
      <div
        ref={ref}
        className={"chartTooltipContentContainer " + (className || "")}
      >
        <div
          style={{
            display: "grid",
            gridGap: "0.375rem",
          }}
        >
          {/* Render heading once */}
          {headingText && (
            <span
              style={{
                fontWeight: "bold",
                marginBottom: 8,
                textAlign: heading?.align || "center",
              }}
            >
              {headingText}
            </span>
          )}

          {/* Render payload items */}
          {payload.map((item) => {
            const barId = item.payload?.barId;
            const itemConfig = barId ? config[barId] : undefined;
            const name =
              (nameKey && item.payload[nameKey]) ||
              itemConfig?.label ||
              item.name;
            const indicatorColor =
              color || itemConfig?.color || item.payload.fill || item.color;

            return (
              <div
                key={item.dataKey}
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    alignItems: indicator === "dot" ? "center" : "",
                  }}
                  className="chartTooltipContent"
                >
                  {!hideIndicator && (
                    <div
                      style={{
                        flexShrink: 0,
                        borderRadius: "2px",
                        borderColor: indicatorColor,
                        backgroundColor: indicatorColor,
                        ...(indicator === "dot" && {
                          height: "10px",
                          width: "10px",
                        }),
                        ...(indicator === "line" && { width: "4px" }),
                        ...(indicator === "dashed" && {
                          width: "0px",
                          border: `1.5px dashed ${indicatorColor}`,
                          backgroundColor: "transparent",
                        }),
                      }}
                    />
                  )}
                  <div
                    style={{
                      display: "flex",
                      flex: "1 1 0%",
                      justifyContent: "space-between",
                      lineHeight: "1",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ color: "hsl(var(--muted-foreground))" }}>
                      {name}
                    </span>
                    {item.value !== null && item.value !== undefined && (
                      <span
                        style={{
                          fontWeight: 500,
                          fontVariantNumeric: "tabular-nums",
                          color: "hsl(var(--foreground))",
                          marginLeft: "15px",
                        }}
                      >
                        {labelFormatter
                          ? labelFormatter(item.value, payload)
                          : item.value.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);
ChartTooltipContent.displayName = "ChartTooltip";

const ChartLegend = RechartsPrimitive.Legend;

const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> &
  Pick<RechartsPrimitive.LegendProps, "payload" | "verticalAlign"> & {
    hideIcon?: boolean;
    nameKey?: string;
  }
>(
  (
    { className, hideIcon = false, payload, verticalAlign = "bottom", nameKey },
    ref
  ) => {
    const { config } = useChart();

    if (!payload?.length) {
      return null;
    }

    return (
      <div
        ref={ref}
        style={{
          paddingBottom: verticalAlign === "top" ? "12px" : 0,
          paddingTop: verticalAlign === "top" ? 0 : "12px",
        }}
        className={"chartLegendContentContainer " + (className || "")}
      >
        {payload.map((item) => {
          const key = `${nameKey || item.dataKey || "value"}`;
          const itemConfig = getPayloadConfigFromPayload(config, item, key);
          return (
            <div key={item.value} className="chartLegendContent">
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <div
                  style={{
                    backgroundColor: item.color,
                    height: "8px",
                    width: "8px",
                    flexShrink: 0,
                    borderRadius: "2px",
                  }}
                />
              )}
              {itemConfig?.label}
            </div>
          );
        })}
      </div>
    );
  }
);
ChartLegendContent.displayName = "ChartLegend";

function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string
) {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }

  const payloadPayload =
    "payload" in payload &&
      typeof payload.payload === "object" &&
      payload.payload !== null
      ? payload.payload
      : undefined;

  let configLabelKey: string = key;

  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === "string"
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string;
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string;
  }

  return configLabelKey in config
    ? config[configLabelKey]
    : config[key as keyof typeof config];
}

export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
};
